<script>
  import { createEventDispatcher, onMount } from 'svelte';
  const dispatch = createEventDispatcher();

  export let label = '';
  export let placeholder = '';
  export let items = [];
  export let selectedValue = '';
  export let searchTerm = '';
  export let isOpen = false;
  export let selectedIndex = -1;

  // Optional props for custom rendering
  export let itemDisplay = (item) => item.label;
  export let getIcon = null;
  export let filterFn = (item, term) => 
    item.label.toLowerCase().includes(term.toLowerCase());

  // Add new padding props
  export let paddingY = '16px'; // Default vertical padding
  export let paddingX = '16px'; // Default horizontal padding

  // Existing props
  export let inputClass = '';
  export let textClass = '';
  export let inputPadding = '48px'; // Default left padding
  export let buttonClass = '';

  onMount(() => {
    if (selectedValue) {
      const selectedItem = items.find(item => item.value === selectedValue);
      if (selectedItem && !searchTerm) {
        searchTerm = selectedItem.label;
      }
    }
  });

  // Declare filteredItems before using it in reactive statements
  let filteredItems = [];

  $: {
    filteredItems = items.filter(item => filterFn(item, searchTerm));
    // Reset selectedIndex if it's out of bounds
    if (selectedIndex >= filteredItems.length) {
      selectedIndex = filteredItems.length > 0 ? 0 : -1;
    }
  }

  // Update autocompleteSuggestion logic
  $: autocompleteSuggestion = isOpen && 
    filteredItems.length > 0 && 
    (searchTerm || isFocused) && 
    selectedIndex >= 0 && 
    selectedIndex < filteredItems.length
      ? (filteredItems[selectedIndex].label.toLowerCase().startsWith(searchTerm.toLowerCase()) 
         ? filteredItems[selectedIndex] 
         : null)
      : null;

  // Add near the top of the script section
  let isFocused = false;

  // Modify the handleFocus and handleBlur functions
  function handleFocus() {
    isFocused = true;
    isOpen = true;
    if (filteredItems.length > 0) {
      selectedIndex = 0;
      // Show first suggestion immediately on focus
      if (!searchTerm) {
        autocompleteSuggestion = filteredItems[0];
      }
    }
  }

  function handleBlur(event) {
    const relatedTarget = event.relatedTarget;
    if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
      isFocused = false;
      isOpen = false;
      selectedIndex = -1;
    }
  }

  function handleKeydown(event) {
    if (!isOpen) {
      isOpen = true;
      // Set initial selection to first item when opening
      if (filteredItems.length > 0) {
        selectedIndex = 0;
      }
    }

    if (isOpen) {
      switch (event.key) {
        case 'Tab':
          if (filteredItems.length === 1) {
            // If there's only one item, select it and allow default tab behavior
            selectedValue = filteredItems[0].value || filteredItems[0].code;
            searchTerm = filteredItems[0].label;
            isOpen = false;
            selectedIndex = -1;
            return;
          }
          // Otherwise, prevent default and handle tab navigation
          event.preventDefault();
          if (filteredItems.length > 0) {
            if (selectedIndex < filteredItems.length - 1) {
              selectedIndex = selectedIndex === -1 ? 0 : selectedIndex + 1;
            } else {
              // Cycle back to first element when at the end
              selectedIndex = 0;
            }
            scrollSelectedIntoView();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          selectedIndex = Math.min(selectedIndex + 1, filteredItems.length - 1);
          scrollSelectedIntoView();
          break;
        case 'ArrowUp':
          event.preventDefault();
          selectedIndex = Math.max(selectedIndex - 1, 0);
          scrollSelectedIntoView();
          break;
        case 'Enter':
          if (filteredItems.length === 1 || selectedIndex >= 0) {
            const item = filteredItems[selectedIndex >= 0 ? selectedIndex : 0];
            selectedValue = item.value || item.code;
            searchTerm = item.label;
            isOpen = false;
            selectedIndex = -1;
            // Updated to include type
            dispatch('select', { type: 'keyboard' });
          }
          break;
        case 'Escape':
          isOpen = false;
          selectedIndex = -1;
          inputElement.blur();
          break;
      }
    }
  }

  function scrollSelectedIntoView() {
    setTimeout(() => {
      const selectedElement = document.querySelector('[data-selected="true"]');
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }, 0);
  }

  // Add this line near the top of the script
  /** @type {HTMLInputElement} */
  export let inputElement;

  // Modify the searchTerm binding to use a custom handler
  let internalSearchTerm = '';
  $: {
    // Update internal value when external value changes
    if (searchTerm !== internalSearchTerm) {
      internalSearchTerm = searchTerm;
    }
  }

  function handleSearchTermChange(event) {
    internalSearchTerm = event.target.value;
    searchTerm = capitalizeFirstLetter(internalSearchTerm);
  }

  function capitalizeFirstLetter(string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : string;
  }
</script>

<svelte:options accessors={true}/>

<div class="relative flex-1">
  <label class="block text-base font-medium text-gray-700 mb-2">
    {label}
  </label>
  <div class="relative">
    <div class="relative flex items-center">
      {#if (autocompleteSuggestion && autocompleteSuggestion.label !== searchTerm) || searchTerm}
        <div class="absolute left-3 flex items-center pointer-events-none w-6 h-4">
          {#if getIcon}
            {@html getIcon(autocompleteSuggestion ? 
              (autocompleteSuggestion.value || autocompleteSuggestion.code) : 
              selectedValue)}
          {/if}
        </div>
        
        {#if autocompleteSuggestion && autocompleteSuggestion.label !== searchTerm}
          <div 
            class="absolute inset-0 flex items-center text-gray-400 pointer-events-none truncate {textClass}"
            style="padding: {paddingY} 40px {paddingY} {inputPadding};"
          >
            {autocompleteSuggestion.label}
          </div>
        {/if}
        <div 
          class="absolute inset-0 flex items-center pointer-events-none truncate {textClass}"
          style="padding: {paddingY} 40px {paddingY} {inputPadding};"
        >
          <span class="text-gray-900 {textClass}">{searchTerm}</span>
        </div>
      {/if}
      
      <input
        bind:this={inputElement}
        type="text"
        class="w-full bg-gray-50 border border-gray-300 rounded-lg pr-10 {textClass} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 !text-transparent caret-black selection:bg-blue-200 transition-all duration-200 hover:border-gray-400 {inputClass}"
        style="padding: {paddingY} 40px {paddingY} {inputPadding};"
        value={searchTerm}
        on:input={handleSearchTermChange}
        on:focus={handleFocus}
        on:blur={handleBlur}
        on:keydown={handleKeydown}
      />

      <!-- Add clear button -->
      {#if searchTerm}
        <button
          type="button"
          class="absolute right-3 p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none"
          on:mousedown|preventDefault={() => {
            searchTerm = '';
            selectedValue = '';
            selectedIndex = -1;
            dispatch('clear');
          }}
          aria-label="Clear search"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </button>
      {:else if !isFocused}
        <div 
          class="absolute inset-0 flex items-center text-gray-400 pointer-events-none {textClass}"
          style="padding-left: {paddingX};"
        >
          {placeholder}
        </div>
      {:else}
        
      {/if}
    </div>

    {#if isOpen}
      <div class="absolute z-10 w-full mt-2 bg-gray-50 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
        {#each filteredItems as item, index}
          <button
            class="w-full text-left {textClass} focus:outline-none transition-colors duration-150 {selectedIndex === index ? 'bg-gray-200' : 'hover:bg-gray-100 focus:bg-gray-100'} {buttonClass}"
            style="padding: {paddingY} {paddingX};"
            data-selected={selectedIndex === index}
            on:mousedown|preventDefault={() => {
              selectedValue = item.value || item.code;
              searchTerm = item.label;
              isOpen = false;
              selectedIndex = -1;
              dispatch('select', { type: 'mouse' });
            }}
          >
            <slot name="item" {item}>
              {itemDisplay(item)}
            </slot>
          </button>
        {/each}
      </div>
    {/if}
  </div>
</div> 